import { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import { IncomingMessage, ServerResponse } from 'http'
import type { SSRConfig } from 'next-i18next'
import { ParsedUrlQuery } from 'querystring'
import { handleTokenParamOnSSR } from '@/auth/utils'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { PayItForwardOfferDataProvider } from '@/contexts/PayItForwardOffersDataContext'
import { getBasicPageMetaData, Seo } from '@/layout/Seo'
import { ContentOnlyLayout } from '@/layouts/ContentOnlyLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { MarketingVideoPopup } from '@/organisms/MarketingVideoPopup'
import { Captcha } from '@/services/Captcha'
import { getOfferById, getOffersForProject, Offer } from '@/services/PifService'
import { getTheatricalRelease } from '@/services/Theatrical'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import DatadogRumFull from '@/utils/datadog/DatadogRumFull'
import { ExpressCheckoutComplete } from './ExpressCheckoutComplete'
import { ExpressCheckoutView } from './components/ExpressCheckoutView'

interface StaticParams {
  params: ParsedUrlQuery
  locale: string
  federationClient: ApolloClient<NormalizedCacheObject>
  next404: () => { notFound: true }
  translations: SSRConfig
}

interface ServerParams extends StaticParams {
  query: ParsedUrlQuery
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> }
  res: ServerResponse
}

export const getExpressCheckoutPageProps = async ({
  params,
  federationClient,
  locale,
  next404,
  region,
  translations,
}: StaticParams & { region?: string }) => {
  const projectSlug = getItemOrFirstEntry(params.projectSlug)
  if (!projectSlug) return next404()

  const [theatricalRelease, offers] = await Promise.all([
    getTheatricalRelease({ theatricalSlug: projectSlug, client: federationClient }),
    getOffersForProject({ projectSlug: projectSlug }, federationClient),
  ])
  if (!theatricalRelease || !offers.length) return next404()

  return {
    props: {
      theatricalRegion: region?.toLowerCase() ?? 'us',
      locale,
      offers,
      projectSlug,
      projectName: theatricalRelease.title ?? '',
      ...translations,
    },
    revalidate: Durations.THIRTY_MINUTES_IN_SECONDS,
  }
}

export const getExpressCheckoutCompletePageProps = async ({
  params,
  query,
  locale,
  federationClient,
  req,
  res,
  next404,
  translations,
}: ServerParams) => {
  const projectSlug = getItemOrFirstEntry(params?.projectSlug)
  const offerId = getItemOrFirstEntry(params?.offerId)

  if (!projectSlug || !offerId) {
    return next404()
  }

  // If a token is present in the query, establish session + cookie and redirect without the token
  const redirect = await handleTokenParamOnSSR(req, res, query)
  if (redirect) {
    return { redirect }
  }

  const [theatricalRelease, offer] = await Promise.all([
    getTheatricalRelease({ theatricalSlug: projectSlug, client: federationClient }),
    getOfferById({ projectSlug, offerId }, federationClient),
  ])

  if (!theatricalRelease || !offer) {
    return next404()
  }

  return {
    props: {
      locale,
      offer,
      projectSlug,
      projectName: theatricalRelease.title ?? '',
      ...translations,
    },
  }
}

export interface ExpressCheckoutPageProps {
  offers: Array<Offer>
  locale: string
  projectSlug: string
  projectName: string
}

export const ExpressCheckoutPage: NextPageWithLayout<ExpressCheckoutPageProps> = ({
  offers,
  projectSlug,
  locale,
  projectName,
}) => {
  const isDavid = projectSlug === slugs.david
  const url = `${paths.getBaseURL()}/express-checkout/${projectSlug}`
  const title = isDavid
    ? 'Help 1 Million Kids See DAVID in Theaters This Christmas'
    : 'Pay it Forward Express Checkout | Angel'
  const description = isDavid
    ? 'Join the movement to send 1 million kids to see DAVID in theaters this Christmas.'
    : `Gift tickets for others to view ${projectName} for free.`

  return (
    <>
      <Captcha />
      <DatadogRumFull />
      {isDavid ? (
        <Seo
          {...getBasicPageMetaData({
            title,
            description,
            cloudinaryImagePath: 'v1761851105/david/open-graph-david-251030b.jpg',
            cloudinaryTransformation: 'c_scale',
            locale,
            path: `express-checkout/${projectSlug}`,
          })}
          noindex
          nofollow
        />
      ) : (
        <Seo
          canonical={url}
          description={description}
          noindex
          nofollow
          openGraph={{ description, title, url }}
          title={title}
        />
      )}
      <MarketingVideoPopup className={'z-[999]'} />
      <PayItForwardOfferDataProvider offers={offers}>
        <ExpressCheckoutView locale={locale} projectSlug={projectSlug} projectName={projectName} />
      </PayItForwardOfferDataProvider>
    </>
  )
}

ExpressCheckoutPage.getLayout = (page) => {
  return <ContentOnlyLayout isDarkMode={false}>{page}</ContentOnlyLayout>
}

export interface ExpressCheckoutCompleteProps {
  offer: Offer
  projectSlug: string
  projectName: string
}

export const ExpressCheckoutCompletePage: NextPageWithLayout<ExpressCheckoutCompleteProps> = ({
  offer,
  projectSlug,
  projectName,
}) => {
  const url = `${paths.getBaseURL()}/express-checkout/${projectSlug}`
  const title = 'Pay it Forward Express Checkout | Angel'
  const description = `Gift tickets for others to view ${projectName} for free.`
  const openGraph = {
    description,
    title,
    url,
  }

  return (
    <>
      <Captcha />
      <DatadogRumFull />
      <Seo canonical={url} description={description} noindex nofollow openGraph={openGraph} title={title} />
      <ExpressCheckoutComplete projectName={projectName} projectSlug={projectSlug} offer={offer} isDarkMode={false} />
    </>
  )
}

ExpressCheckoutCompletePage.getLayout = (page) => {
  return <ContentOnlyLayout isDarkMode={false}>{page}</ContentOnlyLayout>
}
